import { PropsWithChildren } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import * as plansApi from 'api/transactionPlans';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useForceRender } from 'customHooks/useForceRender';
import { accountQueryKeys } from 'queries/account-query-keys';
import { TransactionPlan } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface DeletePlanDialogProps extends PropsWithChildren {
    plan: TransactionPlan;
}

const DeletePlanDialog = (props: DeletePlanDialogProps) => {
    const { success, error } = useToast();
    const { plan, children } = props;
    const { key, forceRender } = useForceRender();
    const { accountId } = useCurrentAccount();
    const queryClient = useQueryClient();

    const { mutate: deletePlan } = useMutation({
        mutationFn: () => plansApi.deletePlan(accountId, plan.id),
        onSuccess() {
            success('Plan deleted');
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.plans(accountId) });
            forceRender();
        },
        onError: (e: GivebutterAxiosError) =>
            error(e.response?.data?.message ?? 'Failed to delete plan'),
    });

    return (
        <AlertDialog.Root key={key} type={'error'}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>Delete recurring plan</AlertDialog.Title>
                        <AlertDialog.Description>
                            Deleting a recurring plan is irreversible. Are you sure?
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                        <AlertDialog.Action onClick={() => deletePlan()}>
                            Delete Plan
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const DeletePlanDialogTrigger = AlertDialog.Trigger;

const Root = DeletePlanDialog;
const Trigger = DeletePlanDialogTrigger;

export {
    DeletePlanDialog,
    DeletePlanDialogTrigger,
    //
    Root,
    Trigger,
};
